"use client";

import { useState, useEffect, useRef } from "react";
import { compareFolders } from "@/app/lib/types";
import type { ProviderInfo, CustomQueryResponse, FolderResponse, ModeInfo } from "@/app/lib/types";
import { FALLBACK_MODES } from "@/app/lib/constants";
import { resolveModeLabel } from "@/app/lib/modes";

// =============================================================================
// Types
// =============================================================================

interface BottomToolbarProps {
  providers: ProviderInfo[];
  modes: ModeInfo[];
  selectedProviders: string[];
  selectedMode: string;
  isStreaming: boolean;
  isTempChat: boolean;
  folderName?: string;
  folderContextSummary?: string;
  folderContextDate?: string;
  sharedWith?: string[];
  sharedBy?: string;
  onToggleProvider: (id: string) => void;
  onReorderProviders: (order: string[]) => void;
  onModeChange: (mode: string) => void;
  onToggleTempChat: () => void;
  onFilesSelected: (files: File[]) => void;
  onCustomQuery: (queryId?: string) => void;
  onAddCustomQuery?: () => void;
  onManageCustomQueries?: () => void;
  onFolderContextClick?: () => void;
  customQueries?: CustomQueryResponse[];
  folders?: FolderResponse[];
  /** Start a new chat inside the chosen folder (preserves any typed draft). */
  onSelectFolder?: (folderId: string) => void;
  isMobile?: boolean;
  onNewChat?: () => void;
}

// =============================================================================
// Constants
// =============================================================================

function shortModeLabel(name: string, modes: readonly ModeInfo[]): string {
  return resolveModeLabel(name, modes);
}

function pickerModes(modes: readonly ModeInfo[]): string[] {
  // Auto is a client-side pseudo-mode, not part of the server-mode fallback.
  if (modes.length === 0) return ["auto", ...FALLBACK_MODES];
  return ["auto", ...[...modes].sort((a, b) => a.order - b.order).map((mode) => mode.name)];
}

const PROVIDER_ABBREV: Record<string, string> = {
  anthropic: "A",
  gemini: "G",
  openai: "O",
};

// =============================================================================
// SVG Icons
// =============================================================================

function PaperclipIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12.5 6.5l-5.8 5.8a2.83 2.83 0 01-4-4L8.9 2.1a1.89 1.89 0 012.7 2.7L5.4 10.9a.94.94 0 01-1.3-1.3L9.5 4.2" />
    </svg>
  );
}

function TextBadgePlusIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M8 5h10M6 10h12M4 15h14" />
      <circle cx="5" cy="5" r="4" fill="var(--bg)" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M5 3v4M3 5h4" strokeWidth="1.4"/>
    </svg>
  );
}

function LightningIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
      <path d="M9.5 1L4 9h4l-1.5 6L13 7H9L9.5 1z" />
    </svg>
  );
}

function BrainIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <path d="M8 14V8M8 8C8 5.5 6 3.5 4.5 3.5S2 5 2 6.5c0 1 .5 2 1.5 2.5M8 8c0-2.5 2-4.5 3.5-4.5S13 5 13 6.5c0 1-.5 2-1.5 2.5" />
      <path d="M3.5 9c-1 .5-1.5 1.5-1.5 2.5 0 1.5 1 2.5 2.5 2.5.8 0 1.5-.3 2-.8M12.5 9c1 .5 1.5 1.5 1.5 2.5 0 1.5-1 2.5-2.5 2.5-.8 0-1.5-.3-2-.8" />
    </svg>
  );
}

function StarIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 1.5l1.85 3.75L14 5.9l-2.98 2.9.7 4.1L8 10.88 4.28 12.9l.7-4.1L2 5.9l4.15-.65L8 1.5z" />
    </svg>
  );
}

function SearchIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5L14 14" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 3.5L5 6.5L7.5 3.5" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 7.5L5.5 10.5L11.5 3.5" />
    </svg>
  );
}

function DragHandleIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor" opacity="0.4">
      <circle cx="4" cy="2.5" r="1" />
      <circle cx="8" cy="2.5" r="1" />
      <circle cx="4" cy="6" r="1" />
      <circle cx="8" cy="6" r="1" />
      <circle cx="4" cy="9.5" r="1" />
      <circle cx="8" cy="9.5" r="1" />
    </svg>
  );
}

function FolderIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor">
      <path d="M1 3.5A1.5 1.5 0 012.5 2h2.764a1.5 1.5 0 011.025.404l1.12 1.04A.5.5 0 007.75 3.6H13.5A1.5 1.5 0 0115 5.1v7.4a1.5 1.5 0 01-1.5 1.5h-11A1.5 1.5 0 011 12.5v-9z" />
    </svg>
  );
}

function TempChatIcon({ active }: { active: boolean }) {
  // 18px = 15 × 1.2 (icon size bumped 20% with the toolbar 32→34 button bump).
  return (
    <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeDasharray={active ? "none" : "2.5 2"}>
      <circle cx="8" cy="8" r="5.5" fill={active ? "currentColor" : "none"} />
      {active && (
        <path d="M5.5 8L7.2 9.7L10.5 6.3" stroke="var(--bg)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );
}

function SparklesIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
      <path d="M8 0l1.5 5.5L15 7l-5.5 1.5L8 14l-1.5-5.5L1 7l5.5-1.5L8 0z" />
      <path d="M12.5 1l.75 2.25L15.5 4l-2.25.75L12.5 7l-.75-2.25L9.5 4l2.25-.75L12.5 1z" opacity="0.6" />
    </svg>
  );
}

function WandIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 14L10.5 5.5M3.5 10.5l2 2M10.75 1.5l.5 1.75L13 3.75l-1.75.5-.5 1.75-.5-1.75-1.75-.5 1.75-.5.5-1.75zM13.25 7.5l.3 1.05 1.05.3-1.05.3-.3 1.05-.3-1.05-1.05-.3 1.05-.3.3-1.05z" />
    </svg>
  );
}

function LayersIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2l6 3.25L8 8.5 2 5.25 8 2z" />
      <path d="M2 8.25L8 11.5l6-3.25M2 11.25L8 14.5l6-3.25" />
    </svg>
  );
}

function ModeIcon({ mode, size = 14 }: { mode: string; size?: number }) {
  switch (mode) {
    case "auto": return <WandIcon size={size} />;
    case "fast": return <LightningIcon size={size} />;
    case "thinking": return <BrainIcon size={size} />;
    case "pro": return <StarIcon size={size} />;
    case "deep_research": return <SearchIcon size={size} />;
    case "instant": return <LightningIcon size={size} />;
    case "low": return <SparklesIcon size={size} />;
    case "medium": return <BrainIcon size={size} />;
    case "high": return <StarIcon size={size} />;
    case "max": return <LayersIcon size={size} />;
    default: return <LightningIcon size={size} />;
  }
}

// =============================================================================
// Popup Hook — close on outside click
// =============================================================================

function usePopup() {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    // Delay to avoid same-click close
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClick);
    };
  }, [isOpen]);

  return { isOpen, setIsOpen, ref };
}

// =============================================================================
// Model Selector Popup
// =============================================================================

function ModelSelectorPopup({
  providers,
  selectedProviders,
  onToggleProvider,
  onReorderProviders,
}: {
  providers: ProviderInfo[];
  selectedProviders: string[];
  onToggleProvider: (id: string) => void;
  onReorderProviders: (order: string[]) => void;
}) {
  const enabledProviders = providers.filter((p) => p.enabled);
  // Order by selectedProviders first, then remaining
  const ordered = [
    ...selectedProviders
      .map((id) => enabledProviders.find((p) => p.id === id))
      .filter(Boolean) as ProviderInfo[],
    ...enabledProviders.filter((p) => !selectedProviders.includes(p.id)),
  ];

  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const handleDragStart = (idx: number) => {
    setDragIdx(idx);
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const newOrder = [...ordered.map((p) => p.id)];
    const [moved] = newOrder.splice(dragIdx, 1);
    newOrder.splice(idx, 0, moved);
    // Only reorder selected ones — keep unselected at end
    const sel = newOrder.filter((id) => selectedProviders.includes(id));
    onReorderProviders(sel);
    setDragIdx(idx);
  };

  const handleDragEnd = () => {
    setDragIdx(null);
  };

  return (
    <div className="toolbar-popup" style={{ right: 0, minWidth: "180px" }}>
      {ordered.map((provider, idx) => {
        const isSelected = selectedProviders.includes(provider.id);
        return (
          <div
            key={provider.id}
            className="toolbar-popup-item"
            draggable
            onDragStart={() => handleDragStart(idx)}
            onDragOver={(e) => handleDragOver(e, idx)}
            onDragEnd={handleDragEnd}
            onClick={() => onToggleProvider(provider.id)}
            style={{
              opacity: dragIdx === idx ? 0.5 : 1,
            }}
          >
            <div
              style={{
                width: "18px",
                height: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isSelected && <CheckIcon />}
            </div>
            <div
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: provider.color,
                flexShrink: 0,
              }}
            />
            <span style={{ flex: 1, fontSize: "13px" }}>{provider.display_name}</span>
            <div
              style={{ cursor: "grab", padding: "0 2px", flexShrink: 0 }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <DragHandleIcon />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// =============================================================================
// Mode Selector Popup
// =============================================================================

function ModeSelectorPopup({
  selectedMode,
  onModeChange,
  modes,
}: {
  selectedMode: string;
  onModeChange: (mode: string) => void;
  modes: readonly ModeInfo[];
}) {
  const availableModes = pickerModes(modes);
  return (
    <div className="toolbar-popup mode-selector-popup" style={{ right: 0 }}>
      {availableModes.map((mode) => (
        <div
          key={mode}
          className="toolbar-popup-item"
          onClick={() => onModeChange(mode)}
          style={{
            fontWeight: selectedMode === mode ? 600 : 400,
          }}
        >
          <div
            style={{
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {selectedMode === mode && <CheckIcon />}
          </div>
          <div style={{ width: "14px", height: "14px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <ModeIcon mode={mode} />
          </div>
          <span style={{ flex: 1, minWidth: 0, fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{mode === "auto" ? "Auto" : resolveModeLabel(mode, modes)}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function BottomToolbar({
  providers,
  modes,
  selectedProviders,
  selectedMode,
  isStreaming,
  isTempChat,
  folderName,
  folderContextDate,
  sharedWith,
  sharedBy,
  onToggleProvider,
  onReorderProviders,
  onModeChange,
  onToggleTempChat,
  onFilesSelected,
  onCustomQuery,
  onAddCustomQuery,
  onManageCustomQueries,
  onFolderContextClick,
  customQueries,
  folders,
  onSelectFolder,
  isMobile,
  onNewChat,
}: BottomToolbarProps) {
  const modelPopup = usePopup();
  const modePopup = usePopup();
  const customQueryPopup = usePopup();
  const folderPopup = usePopup();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Provider abbreviation label for model selector
  const modelLabel = selectedProviders
    .map((id) => PROVIDER_ABBREV[id] || id.charAt(0).toUpperCase())
    .join("/");

  // Mode label
  const currentMode = selectedMode || "auto";

  return (
    <div className="bottom-toolbar-wrapper">
      {/* Toolbar row. Folder/sharing indicator is rendered by page.tsx
          ABOVE the textarea — used to live in the toolbar's center spacer
          but the user wanted it above the input on both mobile and
          desktop. Props (folderName, sharedBy, etc.) are kept on this
          component for now in case a future layout pulls them back here. */}
      <div className="bottom-toolbar">
        {/* Left side */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.pdf,.docx,.xlsx,.csv,.txt,.md"
          style={{ display: "none" }}
          onChange={(e) => {
            const files = Array.from(e.target.files || []);
            if (files.length > 0) {
              onFilesSelected(files);
            }
            e.target.value = "";
          }}
        />
        <button
          className="toolbar-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={isStreaming}
          aria-label="Attach file"
          title="Attach file"
        >
          <PaperclipIcon />
        </button>
        <div style={{ position: "relative" }} ref={customQueryPopup.ref}>
          <button
            className="toolbar-btn"
            onClick={() => !isStreaming && customQueryPopup.setIsOpen(!customQueryPopup.isOpen)}
            disabled={isStreaming}
            aria-label="Custom queries"
            title="Custom queries"
            style={
              isMobile
                ? undefined
                : { width: "auto", borderRadius: "16px", padding: "4px 10px", gap: "4px" }
            }
          >
            <TextBadgePlusIcon />
            {/* Text only on desktop. On mobile we drop the label and keep the
                button as a 34×34 icon-only circle so the bottom row fits one
                line on narrow viewports. */}
            {!isMobile && (
              <span style={{ fontSize: "8.5px", lineHeight: "1.2", textAlign: "center", whiteSpace: "pre-line" }}>
                {"Custom\nQueries"}
              </span>
            )}
          </button>
          {customQueryPopup.isOpen && (
            <div className="toolbar-popup" style={{ left: 0, minWidth: "220px", maxHeight: "300px", overflowY: "auto" }}>
              {customQueries && customQueries.length > 0 ? (
                customQueries.sort((a, b) => a.order - b.order).map(q => (
                  <div key={q.id} className="toolbar-popup-item" onClick={() => { onCustomQuery(q.id); customQueryPopup.setIsOpen(false); }} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                    <span style={{ fontSize: "13px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.name}</span>
                    {q.schedule?.enabled && q.schedule?.frequency && (
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="var(--text-tertiary)" strokeWidth="1.6" style={{ flexShrink: 0 }} aria-label="Scheduled">
                        <circle cx="8" cy="8" r="6.5" />
                        <path d="M8 4.5V8l2.5 1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                ))
              ) : (
                <div className="toolbar-popup-item" style={{ color: "var(--text-tertiary)", fontSize: "13px", cursor: "default" }}>
                  No custom queries
                </div>
              )}
              <div style={{ borderTop: "1px solid var(--border)", marginTop: "4px", paddingTop: "4px" }}>
                <div className="toolbar-popup-item" onClick={() => { onAddCustomQuery?.(); customQueryPopup.setIsOpen(false); }} style={{ fontSize: "12px", color: "var(--accent)", cursor: "pointer" }}>
                  Add custom query
                </div>
                <div className="toolbar-popup-item" onClick={() => { onManageCustomQueries?.(); customQueryPopup.setIsOpen(false); }} style={{ fontSize: "12px", color: "var(--accent)", cursor: "pointer" }}>
                  Manage custom queries
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Folder quick-pick — start a new chat inside a folder in one tap.
            Mirrors the custom-queries dropdown. Solid/active when the current
            chat already belongs to a folder. Selecting a folder preserves the
            typed draft (page.tsx handler does not clear the textarea). */}
        <div style={{ position: "relative" }} ref={folderPopup.ref}>
          <button
            className={`toolbar-btn${folderName ? " active" : ""}`}
            onClick={() => !isStreaming && folderPopup.setIsOpen(!folderPopup.isOpen)}
            disabled={isStreaming}
            aria-label="New chat in folder"
            title="New chat in folder"
          >
            <FolderIcon />
          </button>
          {folderPopup.isOpen && (
            <div className="toolbar-popup" style={{ left: 0, minWidth: "220px", maxHeight: "300px", overflowY: "auto" }}>
              {folders && folders.length > 0 ? (
                [...folders].sort(compareFolders).map((f) => (
                  <div
                    key={f.id}
                    className="toolbar-popup-item"
                    onClick={() => { onSelectFolder?.(f.id); folderPopup.setIsOpen(false); }}
                  >
                    <FolderIcon />
                    <span style={{ fontSize: "13px", marginLeft: "6px" }}>{f.name}</span>
                  </div>
                ))
              ) : (
                <div className="toolbar-popup-item" style={{ color: "var(--text-tertiary)", fontSize: "13px", cursor: "default" }}>
                  No folders
                </div>
              )}
            </div>
          )}
        </div>

        {/* Center spacer — pushes left-side buttons to the left edge and
            right-side buttons to the right edge. The folder/sharing
            indicator that used to render here was relocated above the
            textarea (page.tsx). */}
        <div style={{ flex: 1, minWidth: 0 }} />

        {/* Right side */}

        {/* Single-provider "A" toggle used to render here — moved into
            PromptComposer's button column (v1.6 Batch 7, slot above the
            judge submit). State lives in page.tsx now. */}

        {/* Model selector (A/G/O). Desktop only — on mobile web it's dropped
            to make room (provider-set selection lives in Settings), matching
            the iOS composer which removed this button entirely. The composer
            "A" button is a one-shot fave-provider send (not a mode), so the
            selector is always active. */}
        {!isMobile && (
          <div style={{ position: "relative" }} ref={modelPopup.ref}>
            <button
              className="toolbar-btn toolbar-btn-capsule mode-btn"
              onClick={() => !isStreaming && modelPopup.setIsOpen(!modelPopup.isOpen)}
              disabled={isStreaming}
              aria-label="Select providers"
              title="Select providers"
            >
              <span style={{ fontSize: "13px", fontWeight: 600, minWidth: "42px", textAlign: "center" }}>
                {modelLabel || "..."}
              </span>
              <ChevronDownIcon />
            </button>
            {modelPopup.isOpen && (
              <ModelSelectorPopup
                providers={providers}
                selectedProviders={selectedProviders}
                onToggleProvider={onToggleProvider}
                onReorderProviders={onReorderProviders}
              />
            )}
          </div>
        )}

        {/* Mode selector */}
        <div style={{ position: "relative" }} ref={modePopup.ref}>
          <button
            className="toolbar-btn toolbar-btn-capsule mode-btn"
            onClick={() => !isStreaming && modePopup.setIsOpen(!modePopup.isOpen)}
            disabled={isStreaming}
            aria-label="Select mode"
            title="Select mode"
          >
            <ModeIcon mode={selectedMode} size={13} />
            {/* Fixed label slot: 56px on desktop and 42px on mobile. It holds
                the full mode label and truncates it with a CSS ellipsis, so
                the button keeps a stable width as modes change. */}
            <span style={{
              fontSize: "13px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              width: isMobile ? "42px" : "56px",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-block",
            }}>
              {isMobile ? shortModeLabel(currentMode, modes) : currentMode === "auto" ? "Auto" : resolveModeLabel(currentMode, modes)}
            </span>
            <ChevronDownIcon />
          </button>
          {modePopup.isOpen && (
            <ModeSelectorPopup
              selectedMode={selectedMode}
              modes={modes}
              onModeChange={(mode) => {
                onModeChange(mode);
                modePopup.setIsOpen(false);
              }}
            />
          )}
        </div>

        {/* Temp chat toggle */}
        <button
          className={`toolbar-btn${isTempChat ? " active" : ""}`}
          onClick={onToggleTempChat}
          disabled={isStreaming}
          aria-label="Temporary chat"
          title="Temporary chat"
          style={{ marginLeft: "auto" }}
        >
          <TempChatIcon active={isTempChat} />
        </button>

        {/* New chat — sits directly beneath the composer Send button. Both
            .prompt-composer and .bottom-toolbar share the same parent's
            inner right edge (parent has padding 20px), and neither adds its
            own right padding, so a marginRight: 0 is exactly aligned with
            Send's 34×34 right edge. Icon 18px = 15 × 1.2. */}
        {onNewChat && (
          <button
            className="toolbar-btn"
            onClick={onNewChat}
            aria-label="New chat"
            title="New chat (Cmd+K)"
            style={{ color: "var(--accent)" }}
          >
            {/* 21×21 = 18 × 1.15 (15% bump). transform shifts the glyph
                slightly up-and-right inside the 34×34 button so the pencil
                stroke leans into the corner — matches iOS New Chat icon
                placement (off-center, biased toward the upper-right). */}
            <svg
              width="21"
              height="21"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ transform: "translate(2px, -2px)" }}
            >
              <rect x="2" y="5" width="10.5" height="10.5" rx="2" />
              <path d="M16 2L8 10" />
            </svg>
          </button>
        )}

      </div>
    </div>
  );
}
